import { SphereTooltip } from "@components/common/sphere-tooltip";
import { ArrowDownIcon, blue } from "@flat-ui";
import { Box, Button, ButtonProps, Menu } from "@mui/material";
import { PropsWithChildren, useState } from "react";

interface Props {
  /** The text to show for the button */
  buttonTitle: JSX.Element | string;

  /** True when the button should be disabled */
  isDisabled?: boolean;

  /** Text to show as tooltip */
  tooltipText?: string;

  /**
   * Optional sx properties to override the button style.
   * It accepts a variable to have separated style for when menu is open
   */
  buttonSx?: (isMenuOpen: boolean) => ButtonProps["sx"];

  /** If true, the menu item width (dropdown) will be equal to button width */
  isMenuWidthEqualToButton?: boolean;
}

/** Renders text button having a menu opened as a dropdown */
export function FaroTextMenuButton({
  buttonTitle,
  tooltipText,
  isDisabled,
  children,
  buttonSx,
  isMenuWidthEqualToButton = false,
}: PropsWithChildren<Props>): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  return (
    <SphereTooltip title={tooltipText} shouldShowTooltip={!isMenuOpen}>
      <>
        <Button
          disabled={isDisabled}
          aria-controls={isMenuOpen ? "faro-text-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={!!isMenuOpen}
          variant="text"
          disableElevation
          disableRipple
          draggable={false}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          endIcon={<ArrowDownIcon />}
          sx={{
            fontSize: "12px",
            textTransform: "none",
            color: blue[500],
            borderRadius: "0px",
            "&:hover": {
              backgroundColor: "transparent",
            },
            "&:focus": {
              backgroundColor: "transparent",
            },
            ...buttonSx?.(isMenuOpen),
          }}
        >
          {buttonTitle}
        </Button>
        <Menu
          id="faro-text-menu"
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={() => setAnchorEl(null)}
          slotProps={{
            list: {
              sx: {
                width: isMenuWidthEqualToButton
                  ? anchorEl && anchorEl.offsetWidth
                  : "225px",
              },
            },
          }}
        >
          <Box
            component="div"
            onClick={() => setAnchorEl(null)}
            sx={{
              padding: "0px 8px",
              "& .MuiList-root": {
                // Fix bottom padding, since each menu item has a bottom margin of 2px
                paddingBottom: "6px",
              },
            }}
          >
            {children}
          </Box>
        </Menu>
      </>
    </SphereTooltip>
  );
}
